import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Header from "../../components/CommonComponents/Header";
import axiosInstance from "../../../Fbaseurl";
import "./index.css";

const decodeFromBase64 = (value) => {
  if (!value) return null;
  try {
    const normalized = decodeURIComponent(value);
    if (typeof window !== "undefined" && typeof window.atob === "function") {
      const decoded = window.atob(normalized);
      return JSON.parse(decoded);
    }
    if (typeof Buffer !== "undefined") {
      return JSON.parse(Buffer.from(normalized, "base64").toString("utf-8"));
    }
  } catch (err) {
    console.warn("Failed to decode payload:", err);
  }
  return null;
};

const BusinessCreditDetails = () => {
  const { prospectBusinessId } = useParams();
  const [searchParams] = useSearchParams();
  const [businessSummary, setBusinessSummary] = useState(null);
  const [projectedCredit, setProjectedCredit] = useState("");
  const [eligibleCredits, setEligibleCredits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const payloadParam = searchParams.get("payload");
    const decoded = payloadParam ? decodeFromBase64(payloadParam) : null;
    const fallbackSummary = {
      businessName: searchParams.get("name") || "",
      address: searchParams.get("address") || "",
      employees: searchParams.get("employees") || "",
      annualHires: searchParams.get("annualHires") || "",
      projectedCredit: searchParams.get("projectedCredit") || "",
    };
    const merged = {
      businessName:
        decoded?.businessName || fallbackSummary.businessName || "Selected business",
      address: decoded?.address || fallbackSummary.address || "",
      employees: decoded?.employees || fallbackSummary.employees || "",
      annualHires: decoded?.annualHires || fallbackSummary.annualHires || "",
      annualHiresOverride:
        typeof decoded?.annualHiresNumeric === "number"
          ? decoded.annualHiresNumeric
          : Number.isFinite(Number(searchParams.get("annualHiresNumeric")))
          ? Number(searchParams.get("annualHiresNumeric"))
          : undefined,
    };
    setBusinessSummary(merged);
    setProjectedCredit(
      decoded?.projectedCredit ||
        fallbackSummary.projectedCredit ||
        "No Data Available"
    );
  }, [searchParams]);

  useEffect(() => {
    if (!prospectBusinessId || !businessSummary) return;
    let isMounted = true;
    setIsLoading(true);
    setError("");
    const payload = [
      {
        prospectBusinessId,
      },
    ];
    if (
      typeof businessSummary.annualHiresOverride === "number" &&
      !Number.isNaN(businessSummary.annualHiresOverride)
    ) {
      payload[0].annualHiresOverride = businessSummary.annualHiresOverride;
    }
    const fetchCredits = async () => {
      try {
        const response = await axiosInstance.post("/taxCredits/lookup/v2", payload);
        const data = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        const matched = data.find(
          (item) =>
            String(item.prospectBusinessId) === String(prospectBusinessId)
        );
        if (!isMounted) return;
        if (matched) {
          setEligibleCredits(matched.availableTaxCredits || []);
          setProjectedCredit((prev) =>
            typeof matched.totalProjectedTaxCreditAmount === "number"
              ? matched.totalProjectedTaxCreditAmount
              : prev
          );
        } else {
          setEligibleCredits([]);
        }
      } catch (err) {
        if (!isMounted) return;
        setEligibleCredits([]);
        setError("Failed to load eligible credits. Please try again later.");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    fetchCredits();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospectBusinessId, businessSummary?.annualHiresOverride]);

  const formattedProjectedCredit = useMemo(() => {
    if (projectedCredit === null || typeof projectedCredit === "undefined") {
      return "No Data Available";
    }
    if (typeof projectedCredit === "number") {
      return projectedCredit.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      });
    }
    const numeric = Number(
      String(projectedCredit).replace(/[^0-9.-]+/g, "")
    );
    if (!Number.isNaN(numeric)) {
      return numeric.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      });
    }
    return projectedCredit;
  }, [projectedCredit]);

  const businessName = businessSummary?.businessName || "Selected business";
  const address =
    businessSummary?.address || "No address details were provided for this business.";
  const employees = businessSummary?.employees || "No Data Available";
  const annualHires = businessSummary?.annualHires || "No Data Available";

  return (
    <div className="credit-detail-wrapper">
      <Header />
      <div className="credit-detail-content">
        <div className="credit-detail-card">
          <div className="credit-detail-heading">
            <h1>{businessName}</h1>
            <p>{address}</p>
          </div>
          <div className="credit-detail-grid">
            <div>
              <span className="credit-detail-label">Employees</span>
              <div className="credit-detail-value">{employees}</div>
            </div>
            <div>
              <span className="credit-detail-label">Annual hires</span>
              <div className="credit-detail-value">{annualHires}</div>
            </div>
            <div>
              <span className="credit-detail-label">Projected credit</span>
              <div className="credit-detail-value">{formattedProjectedCredit}</div>
            </div>
          </div>
        </div>

        <div className="credit-detail-card">
          <div className="credit-detail-heading mb-4">
            <h2>Eligible Credits</h2>
          </div>
          {isLoading ? (
            <div className="credit-detail-loader">
              <CircularProgress size={32} thickness={4} />
            </div>
          ) : error ? (
            <div className="credit-detail-error">{error}</div>
          ) : eligibleCredits.length ? (
            <div className="credit-detail-list">
              {eligibleCredits.map((credit, index) => (
                <div className="credit-detail-list-item" key={credit?.name || index}>
                  <div className="credit-detail-credit-name">
                    {credit?.name || "Unnamed credit"}
                  </div>
                  {credit?.category && (
                    <div className="credit-detail-credit-category">
                      Category: {credit.category}
                    </div>
                  )}
                  <div className="credit-detail-credit-description">
                    {credit?.description || "No description available for this credit."}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="credit-detail-empty">
              No eligible credits were returned for this business.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessCreditDetails;
